// Kraken PP — training load & 1RM math helpers

// RPE / RIR — barème RTS (Mike Tuchscherer)
// RPE 10 = 0 rep en réserve · 9 = 1 RIR · 8 = 2 RIR · 7 = 3 RIR · 6 = 4 RIR
export const RPE_SCALE = [
  { rpe: "10", rir: "0", desc: "Échec technique · aucune rep en réserve" },
  { rpe: "9,5", rir: "0–1", desc: "Peut-être 1 rep de plus" },
  { rpe: "9", rir: "1", desc: "1 rep en réserve certaine" },
  { rpe: "8,5", rir: "1–2", desc: "1 rep sûre, peut-être 2" },
  { rpe: "8", rir: "2", desc: "2 reps en réserve" },
  { rpe: "7,5", rir: "2–3", desc: "2 sûres, peut-être 3" },
  { rpe: "7", rir: "3", desc: "3 reps en réserve · barre rapide" },
  { rpe: "6", rir: "4+", desc: "Travail léger / technique" },
];

// RPE cible recommandé selon l'intensité (%1RM)
export const rpeForPct = (pct) => {
  if (pct >= 93) return { rpe: "9–10", rir: "0–1" };
  if (pct >= 87) return { rpe: "8–9", rir: "1–2" };
  if (pct >= 80) return { rpe: "7–8", rir: "2–3" };
  return { rpe: "6–7", rir: "3–4" };
};

// Grille RTS : Reps @ RPE → % du 1RM
export const RPE_REPS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
export const RPE_GRID = [
  { rpe: "10", rir: "0", pcts: [100, 95.5, 92.2, 89.2, 86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0] },
  { rpe: "9,5", rir: "0–1", pcts: [97.8, 93.9, 90.7, 87.8, 85.0, 82.4, 79.9, 77.4, 75.1, 72.3, 69.4, 66.7] },
  { rpe: "9", rir: "1", pcts: [95.5, 92.2, 89.2, 86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0, 65.3] },
  { rpe: "8,5", rir: "1–2", pcts: [93.9, 90.7, 87.8, 85.0, 82.4, 79.9, 77.4, 75.1, 72.3, 69.4, 66.7, 64.0] },
  { rpe: "8", rir: "2", pcts: [92.2, 89.2, 86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0, 65.3, 62.6] },
  { rpe: "7,5", rir: "2–3", pcts: [90.7, 87.8, 85.0, 82.4, 79.9, 77.4, 75.1, 72.3, 69.4, 66.7, 64.0, 61.3] },
  { rpe: "7", rir: "3", pcts: [89.2, 86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0, 65.3, 62.6, 59.9] },
  { rpe: "6,5", rir: "3–4", pcts: [87.8, 85.0, 82.4, 79.9, 77.4, 75.1, 72.3, 69.4, 66.7, 64.0, 61.3, 58.6] },
  { rpe: "6", rir: "4", pcts: [86.3, 83.7, 81.1, 78.6, 76.2, 73.9, 70.7, 68.0, 65.3, 62.6, 59.9, 57.2] },
];

export const NSCA_ROWS = [
  { pct: 100, reps: 1, zone: "Force Max", color: "#ef4444" },
  { pct: 95, reps: 2, zone: "Force Max", color: "#f87171" },
  { pct: 93, reps: 3, zone: "Force / Puissance", color: "#f87171" },
  { pct: 90, reps: 4, zone: "Force / Puissance", color: "#fb923c" },
  { pct: 87, reps: 5, zone: "Force / Puissance", color: "#fb923c" },
  { pct: 85, reps: 6, zone: "Hypertrophie / Force", color: "#fbbf24" },
  { pct: 83, reps: 7, zone: "Hypertrophie", color: "#facc15" },
  { pct: 80, reps: 8, zone: "Hypertrophie", color: "#facc15" },
  { pct: 77, reps: 9, zone: "Hypertrophie", color: "#60a5fa" },
  { pct: 75, reps: 10, zone: "Hypertrophie / Volume", color: "#60a5fa" },
  { pct: 70, reps: 12, zone: "Endurance Musc.", color: "#3b82f6" },
];

// 1RM estimation formulas
export const epley1RM = (weight, reps) =>
  reps <= 1 ? weight : weight * (1 + reps / 30);

export const brzycki1RM = (weight, reps) => {
  if (reps <= 1) return weight;
  if (reps >= 37) return weight * 10;
  return weight * (36 / (37 - reps));
};

export const lombardi1RM = (weight, reps) =>
  reps <= 1 ? weight : weight * Math.pow(reps, 0.1);

export const average1RM = (weight, reps) =>
  (epley1RM(weight, reps) + brzycki1RM(weight, reps)) / 2;

export const FORMULAS = {
  epley: { label: "Epley", fn: epley1RM, eq: "1RM = P × (1 + R / 30)" },
  brzycki: { label: "Brzycki", fn: brzycki1RM, eq: "1RM = P × (36 / (37 − R))" },
  average: {
    label: "Moyenne",
    fn: average1RM,
    eq: "Moyenne d'Epley & Brzycki",
  },
};

// Compute XRM from a projected 1RM (inverse of formulas — approximate via Epley)
// Given a 1RM, weight that allows N reps ≈ 1RM / (1 + N/30) [Epley]
export const weightForReps = (oneRM, reps) =>
  reps <= 1 ? oneRM : oneRM / (1 + reps / 30);

// Round to nearest step (2.5 or 5)
export const roundTo = (value, step) => {
  if (!step || step <= 0) return value;
  return Math.round(value / step) * step;
};

// Unit conversion
export const LBS_PER_KG = 2.2046226218;
export const kgToLbs = (kg) => kg * LBS_PER_KG;
export const lbsToKg = (lbs) => lbs / LBS_PER_KG;

// Format a number with fixed digits without trailing zeros
export const fmt = (v, digits = 1) => {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  const rounded = Number(v).toFixed(digits);
  return rounded.replace(/\.0+$/, "");
};

// Plate loading — profils d'équipement
export const PLATES_LBS = [45, 35, 25, 10, 5, 2.5];
export const PLATES_KG = [25, 20, 15, 10, 5, 2.5, 1.25];

export const PLATE_PROFILES = {
  commercial: {
    key: "commercial",
    label: "Gym commerciale",
    short: "Commerciale",
    unit: "lbs",
    plates: [45, 35, 25, 10, 5, 2.5],
    bars: [
      { w: 45, label: "Barre olympique 45 lb" },
      { w: 35, label: "Barre femme 35 lb" },
    ],
    collar: 2.5,
    collarLabel: "Colliers à ressort (2,5 lb / côté)",
    note: "Disques en fonte / bumpers standards en livres",
  },
  ipf: {
    key: "ipf",
    label: "Powerlifting IPF",
    short: "IPF",
    unit: "kg",
    plates: [25, 20, 15, 10, 5, 2.5, 1.25, 0.5, 0.25],
    bars: [
      { w: 20, label: "Barre IPF 20 kg (H)" },
      { w: 15, label: "Barre IPF 15 kg (F)" },
    ],
    collar: 2.5,
    collarLabel: "Colliers de compétition (2,5 kg / côté)",
    note: "Disques calibrés acier IPF · précision 0,25 kg",
  },
  iwf: {
    key: "iwf",
    label: "Haltérophilie IWF",
    short: "IWF",
    unit: "kg",
    plates: [25, 20, 15, 10, 5, 2.5, 1.25, 0.5],
    bars: [
      { w: 20, label: "Barre IWF 20 kg (H)" },
      { w: 15, label: "Barre IWF 15 kg (F)" },
    ],
    collar: 2.5,
    collarLabel: "Colliers IWF (2,5 kg / côté)",
    note: "Bumpers de compétition IWF · barre à roulement",
  },
};

// Calcule le chargement par côté pour un profil d'équipement donné.
// target/targetUnit = charge visée dans l'unité de l'app; conversion auto vers l'unité du profil.
export const computeLoadout = ({
  target,
  targetUnit,
  profileKey = "commercial",
  bar,
  collars = false,
}) => {
  const profile = PLATE_PROFILES[profileKey] || PLATE_PROFILES.commercial;
  const unit = profile.unit;
  let goal = toNum(target);
  if (targetUnit !== unit) {
    goal = unit === "kg" ? lbsToKg(goal) : kgToLbs(goal);
  }
  const barWeight = bar ?? profile.bars[0].w;
  const collarWeight = collars ? profile.collar : 0;
  const base = barWeight + collarWeight * 2;

  if (goal < base) {
    return {
      profile,
      unit,
      bar: barWeight,
      collarWeight,
      perSide: [],
      remainder: 0,
      total: base,
      goal,
      under: true,
    };
  }

  let perSide = (goal - base) / 2;
  const result = [];
  for (const p of profile.plates) {
    let count = 0;
    while (perSide >= p - 1e-6) {
      perSide -= p;
      count += 1;
    }
    if (count > 0) result.push({ plate: p, count });
  }
  const loaded =
    base + result.reduce((s, r) => s + r.plate * r.count, 0) * 2;
  return {
    profile,
    unit,
    bar: barWeight,
    collarWeight,
    perSide: result,
    remainder: perSide,
    total: loaded,
    goal,
    under: false,
  };
};

export const computePlates = (totalWeight, unit) => {
  const r = computeLoadout({
    target: totalWeight,
    targetUnit: unit,
    profileKey: unit === "kg" ? "ipf" : "commercial",
  });
  return { bar: r.bar, perSide: r.perSide, remainder: r.remainder };
};

// Standard RM points to display in the "toutes les RM" panel
export const RM_POINTS = [1, 2, 3, 5, 6, 8, 10, 12, 15, 20];

// Coerce user input (may be "", null, undefined, number, string) to a safe number for calculations
export const toNum = (v) => {
  if (v === "" || v === null || v === undefined) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
